import { useEffect, useRef, useState, type ReactNode } from "react";
import { Share2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@apollo/client/react";

import { Button } from "@lytra/frontend-common/components/v2/ui/button";
import { ForwardComposer, type ForwardMessage } from "./ForwardComposer";
import { useForwardDrafts } from "./forwardDraft";
import { TICKET_FORWARD_MAIL_ENABLED } from "./forwardQueries";
import type { PickerAgent } from "./MentionList";
import { latestForwardableMessage } from "./noteThreads";
import type { TicketMessage } from "./noteThreads";
import type { AgentsState } from "./recipients";
import { useLiveAnnouncement } from "./useLiveAnnouncement";

/** What the host reads of a thread row: what the composer shows, and what decides the button. */
export type ForwardHostMessage = ForwardMessage &
  Pick<TicketMessage, "isNote" | "isForward" | "isInternal">;

export interface ForwardHostOptions {
  /** The thread's messages, oldest first. */
  messages: readonly ForwardHostMessage[];
  /** The ticket the composer shares from; without it there is nothing to share. */
  conversationId?: string;
  /** The tenant's colleagues (the `agents` query of the ticket). */
  agents: readonly PickerAgent[];
  agentsState: AgentsState;
  /** Without notes there is no "Mit Kollegen teilen": no button, no composer. */
  enableNotes?: boolean;
}

export interface ForwardHost {
  /**
   * The "Mit Kollegen teilen" button of the thread's foot or, while it is open, the inline
   * composer in its place; null where there is nothing to share.
   */
  action: ReactNode;
  /** The live region that confirms a send; null where notes are not enabled. */
  region: ReactNode;
}

/**
 * The host side of "Mit Kollegen teilen" (#717) for the ticket view: one labelled button at
 * the foot of the thread that shares the newest message `canForwardMessage` allows (inbound
 * or outbound mail, never a note, a send or an internal row), the inline composer that takes
 * its place, focus back on the button when it closes, and a polite confirmation after a send.
 * The drafts and the mail switch live here, so they outlast a closed composer for as long as
 * the ticket view is mounted. Sharing with a colleague is not a customer reply: it stays
 * available without the editor (D-05), only where notes are enabled.
 */
export function useForwardHost({
  messages,
  conversationId,
  agents,
  agentsState,
  enableNotes,
}: ForwardHostOptions): ForwardHost {
  const { t } = useTranslation();
  const [messageId, setMessageId] = useState<string | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const returnFocusRef = useRef(false);
  // One draft per message while the ticket view is mounted (UI-SPEC "Draft handling").
  const drafts = useForwardDrafts();
  // Asked once while the ticket view is mounted. Until the answer is in (or when it fails) no
  // mail is offered: the server refuses mail recipients while it is off anyway.
  const { data: mailData } = useQuery(TICKET_FORWARD_MAIL_ENABLED, {
    errorPolicy: "all",
    skip: !enableNotes,
  });
  const mailEnabled = mailData?.forwardMailEnabled ?? false;
  // Announced on every send, also a second one with the same count.
  const { announce, region } = useLiveAnnouncement();

  // The composer is gone; the button it replaced takes the focus back.
  useEffect(() => {
    if (messageId === null && returnFocusRef.current) {
      returnFocusRef.current = false;
      buttonRef.current?.focus();
    }
  }, [messageId]);

  if (!enableNotes || !conversationId) return { action: null, region: null };

  const open = messageId
    ? messages.find((m) => m.id === messageId)
    : undefined;
  const latest = latestForwardableMessage(messages);
  const close = () => {
    returnFocusRef.current = true;
    setMessageId(null);
  };

  const action = open ? (
    <ForwardComposer
      message={open}
      conversationId={conversationId}
      drafts={drafts}
      mailEnabled={mailEnabled}
      agents={agents}
      agentsState={agentsState}
      onClose={close}
      onSent={(count) => announce(t("tickets.forward.status.sent", { count }))}
    />
  ) : latest ? (
    <Button
      ref={buttonRef}
      type="button"
      variant="default"
      size="md"
      leadingIcon={Share2}
      onClick={() => setMessageId(latest.id)}
    >
      {t("tickets.forward.trigger")}
    </Button>
  ) : null;

  return { action, region };
}
